import { type ReactNode } from "react";
import { useFormContext } from "react-hook-form";

import { type Address } from "@/components/address-search";
import type { ExtendedForm } from "@/features/payment-methods/hooks/use-available-payment-methods";
import { removeSpacesAndHyphens, stripTextAfterHyphen } from "@/lib/utils";

import { AddressFieldsContext } from "./use-address-fields";

interface AddressFieldsProviderProps {
  children: ReactNode;
  form: ExtendedForm;
}

export const AddressFieldsProvider = ({
  children,
  form,
}: AddressFieldsProviderProps) => {
  const { setValue } = useFormContext();

  const fillAddressFields = (address: Address) => {
    const stateField = form.fields.find(
      (field) => field.property_name === "state"
    );

    // Set address line 1
    if (address.Line1) {
      setValue("line_1", address.Line1);
    }

    // Set city - direct text value
    if (address.City) {
      setValue("city", address.City);
    }

    // Set state - match by ProvinceCode to key
    if (address.ProvinceCode && stateField?.options) {
      const matchedState = stateField.options.find(
        (option) => option.key === address.ProvinceCode
      );
      if (matchedState) {
        setValue("state", matchedState.key);
      }
    }

    // Set postal code - with formatting
    if (address.PostalCode) {
      setValue(
        "zip",
        removeSpacesAndHyphens(stripTextAfterHyphen(address.PostalCode))
      );
    }
  };

  return (
    <AddressFieldsContext.Provider value={{ fillAddressFields }}>
      {children}
    </AddressFieldsContext.Provider>
  );
};
